// Clip fetched layer features to the zone's bounding box before any turf work.
//
// Why this exists: the WFS BBOX filter (and the ArcGIS envelope query) tests a
// feature's BOUNDING BOX, not its geometry. A single Norfolk field therefore
// pulls in whole coastal multipolygons — Outer Thames Estuary and Greater Wash,
// one of which is a single ring of 515,045 vertices — that never come near the
// field. clipToZone then spends seconds intersecting them to return nothing.
//
// Clipping to the zone bbox first is ANSWER-PRESERVING BY CONSTRUCTION: the zone
// is a subset of its own bounding box, so anything the clip discards could never
// have intersected the zone. Measured on a real Norfolk field, zone-open blocking
// compute went from 9,924 ms to 101 ms with every class code and area fraction
// identical to nine decimal places.
//
// Sutherland-Hodgman against the four bbox edges, no dependency. Turf is not used
// here on purpose: the point is to avoid paying for turf on geometry we are about
// to throw away.

/**
 * Clip a single closed linear ring to a bbox.
 * @param {Array<Array<number>>} ring closed ring of [lng, lat] pairs
 * @param {Array<number>} bbox [west, south, east, north]
 * @returns {Array<Array<number>>|null} clipped closed ring, or null when the
 *   ring falls wholly outside the bbox or degenerates to fewer than 3 vertices.
 */
function clipRingToBbox(ring, [west, south, east, north]) {
  // One [insideTest, edgeIntersection] pair per bbox edge, applied in turn.
  const edges = [
    [
      (p) => p[0] >= west,
      (p, q) => {
        const t = (west - p[0]) / (q[0] - p[0]);
        return [west, p[1] + t * (q[1] - p[1])];
      },
    ],
    [
      (p) => p[0] <= east,
      (p, q) => {
        const t = (east - p[0]) / (q[0] - p[0]);
        return [east, p[1] + t * (q[1] - p[1])];
      },
    ],
    [
      (p) => p[1] >= south,
      (p, q) => {
        const t = (south - p[1]) / (q[1] - p[1]);
        return [p[0] + t * (q[0] - p[0]), south];
      },
    ],
    [
      (p) => p[1] <= north,
      (p, q) => {
        const t = (north - p[1]) / (q[1] - p[1]);
        return [p[0] + t * (q[0] - p[0]), north];
      },
    ],
  ];

  // Work on the open ring (no repeated first vertex) and re-close at the end.
  let current = ring.slice(0, -1);

  for (const [isInside, intersection] of edges) {
    const next = [];
    for (let i = 0; i < current.length; i += 1) {
      const point = current[i];
      const previous = current[(i + current.length - 1) % current.length];
      const pointInside = isInside(point);
      const previousInside = isInside(previous);
      if (pointInside) {
        if (!previousInside) next.push(intersection(previous, point));
        next.push(point);
      } else if (previousInside) {
        next.push(intersection(previous, point));
      }
    }
    current = next;
    if (!current.length) return null;
  }

  if (current.length < 3) return null;
  return [...current, current[0]];
}

/**
 * Clip one fetched feature to a bbox.
 *
 * Non-polygonal geometry is returned untouched: the clip is an optimisation for
 * the expensive polygon path, not a filter, and dropping anything else here
 * would change answers. Interior rings are clipped alongside the outer ring, so
 * a hole that survives the clip stays a hole (BBB-454 was an interior-ring bug).
 *
 * @param {object} feature GeoJSON Feature
 * @param {Array<number>} bbox [west, south, east, north]
 * @returns {object|null} the clipped Feature, the original Feature when its
 *   geometry is not polygonal, or null when nothing survives the clip.
 */
export function bboxClipFeature(feature, bbox) {
  const geometry = feature?.geometry;
  if (!geometry) return null;
  if (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon") return feature;

  const parts =
    geometry.type === "MultiPolygon" ? geometry.coordinates : [geometry.coordinates];

  const kept = [];
  for (const part of parts) {
    const outer = clipRingToBbox(part[0], bbox);
    // An outer ring outside the bbox takes its holes with it.
    if (!outer) continue;
    const rings = [outer];
    for (let i = 1; i < part.length; i += 1) {
      const hole = clipRingToBbox(part[i], bbox);
      if (hole) rings.push(hole);
    }
    kept.push(rings);
  }

  if (!kept.length) return null;

  // Always MultiPolygon out, even from a Polygon in: a Polygon can clip into
  // several disjoint pieces, and everything downstream takes either.
  return { ...feature, geometry: { type: "MultiPolygon", coordinates: kept } };
}

// One degree of latitude, in metres. Good to about 0.5% anywhere in the UK,
// which at the 250 m scale this is used for is a few metres. Nothing here needs
// a projection: the grown box is a scan window, not a measurement.
const METRES_PER_DEGREE_LAT = 111320;

/**
 * Grow a bbox outwards by a distance in metres.
 *
 * Longitude is scaled by cos(latitude) at the box's mid-latitude, so the grown
 * box is roughly the same real distance out on all four sides rather than
 * stretching east-west the further north the field is.
 *
 * @param {Array<number>} bbox [west, south, east, north]
 * @param {number} metres distance to grow by on every side
 * @returns {Array<number>} the grown bbox
 */
export function growBbox([west, south, east, north], metres) {
  if (!metres) return [west, south, east, north];
  const latDelta = metres / METRES_PER_DEGREE_LAT;
  const midLat = (south + north) / 2;
  const cosLat = Math.cos((midLat * Math.PI) / 180);
  // Guard the poles, where cos goes to zero and the longitude delta explodes.
  const lngDelta = metres / (METRES_PER_DEGREE_LAT * Math.max(cosLat, 0.01));
  return [west - lngDelta, south - latDelta, east + lngDelta, north + latDelta];
}

/**
 * The bbox as a closed GeoJSON Polygon, for use as a query geometry or as a
 * clip target for turf.
 * @param {Array<number>} bbox [west, south, east, north]
 * @returns {object} GeoJSON Polygon geometry
 */
export function bboxPolygon([west, south, east, north]) {
  return {
    type: "Polygon",
    coordinates: [
      [
        [west, south],
        [east, south],
        [east, north],
        [west, north],
        [west, south],
      ],
    ],
  };
}

/**
 * Clip a list of fetched features to a bbox, dropping the ones that fall wholly
 * outside it.
 * @param {Array} features GeoJSON Features
 * @param {Array<number>} bbox [west, south, east, north]
 * @returns {Array} surviving (clipped) Features
 */
export function bboxClipFeatures(features, bbox) {
  const kept = [];
  for (const feature of features || []) {
    const clipped = bboxClipFeature(feature, bbox);
    if (clipped) kept.push(clipped);
  }
  return kept;
}
